// ══════════════════════════════════════════════════════════════════════════════════════════════
// LE GROUPE DE MISE À JOUR D'UN MAGASIN — choisi ici, par application, suivi par ses appareils (§32.6ter).
//
// POURQUOI ICI. Au store, l'opérateur ne fait plus qu'un geste : télécharger. L'application reçoit son
// groupe de la Console — au scan de mise en service, puis à chaque changement (synchronisation pour la
// caisse, tirage de configuration pour Mobile Partner). Un magasin qui obtient son propre groupe le
// fait donc suivre à ses appareils sans que personne re-scanne quoi que ce soit.
//
// ⚠️ UN SÉLECTEUR PAR APPLICATION : chaque application a son flux public et ses propres groupes, sans
//    rapport entre eux — un groupe de la caisse n'existe pas chez Mobile Partner.
// ⚠️ « AUCUN GROUPE » EST TOUJOURS PROPOSÉ (décision utilisateur) : certains équipements ne passent que par leur propre
//    store, et on ne peut pas les forcer sur le nôtre. Il ne dépend pas du store — il dit justement de ne pas le suivre.
// ⚠️ LA LISTE EST CELLE DU STORE, LUE À L'OUVERTURE. Un store muet laisse le seul flux public, et le DIT ;
//    un groupe choisi puis supprimé au store reste affiché sous son ancien nom, marqué comme disparu —
//    les appareils gardent alors le groupe qu'ils suivaient, ce qui se dit aussi (§23.0).
// ══════════════════════════════════════════════════════════════════════════════════════════════
import { useCallback, useEffect, useId, useState, type CSSProperties, type ReactNode } from 'react';
import { alert, showToast, useLatest } from '@orgazone/ui-web';
import { NO_PRIVATE_STORE_UPDATES } from '@orgazone/sync-contract';
import { applicationOf } from '@orgazone/applications';
import { api, type StoreGroup, type UpdateGroupChoice } from '../api';

/** Le nom du choix « aucun groupe » — l'option, le récapitulatif de l'assistant et le compte rendu le disent ainsi. */
export const NO_GROUP_LABEL = 'Aucun groupe — le store de l’appareil';

/** Les groupes d'une application, tels que le store les publie — `error` = le store n'a pas répondu. */
export function useAppGroups(slug: string): { groups: StoreGroup[] | null; error: string | null } {
  const [state, setState] = useState<{ groups: StoreGroup[] | null; error: string | null }>({ groups: null, error: null });
  useEffect(() => {
    let alive = true;
    api.storeAppGroups(slug)
      .then((r) => { if (alive) setState({ groups: r.groups, error: r.error }); })
      .catch((e) => { if (alive) setState({ groups: [], error: (e as Error).message }); });
    return () => { alive = false; };
  }, [slug]);
  return state;
}

/** Ce qu'un groupe sert, dit à côté de son nom — c'est ce qui fait choisir. */
const servedLabel = (g: StoreGroup): string => (g.servedVersion ? `version ${g.servedVersion}` : 'suit le flux public');

const selectSt: CSSProperties = { width: '100%', height: 34, padding: '0 10px', borderRadius: 'var(--r-sm)', border: '1px solid var(--ink200)', background: 'var(--canvas)', color: 'var(--ink900)', fontSize: 'var(--t-body)' };

/**
 * Le choix du groupe d'UNE application. `value` = jeton d'inscription, `''` = flux public, `NO_PRIVATE_STORE_UPDATES` = aucun.
 * ⚠️ Un jeton choisi qui n'est plus dans la liste reste une option, nommée « n'existe plus au store » :
 *    le retirer en silence ferait croire que le magasin suit déjà le flux public.
 */
export function UpdateGroupSelect({ label, groups, error, value, staleName, onChange, disabled, action }: {
  label: string; groups: StoreGroup[] | null; error: string | null; value: string; staleName?: string | null;
  onChange: (enrollToken: string) => void; disabled?: boolean;
  /** Le geste posé à côté de la liste (« Enregistrer » dans la fiche) — l'assistant n'en a pas. */
  action?: ReactNode;
}) {
  const id = useId();
  const noGroup = value === NO_PRIVATE_STORE_UPDATES;
  const stale = value !== '' && !noGroup && !!groups && !groups.some((g) => g.enrollToken === value);
  return (
    <div>
      <label htmlFor={id} style={{ display: 'block', fontSize: 'var(--t-sub)', color: 'var(--ink500)', marginBottom: 4 }}>{label}</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <select id={id} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled || groups === null} style={{ ...selectSt, flex: 1 }}>
          <option value="">Flux public de l’application</option>
          <option value={NO_PRIVATE_STORE_UPDATES}>{NO_GROUP_LABEL}</option>
          {(groups ?? []).map((g) => <option key={g.enrollToken} value={g.enrollToken}>{g.name} — {servedLabel(g)}</option>)}
          {stale && <option value={value}>{staleName ?? 'Groupe'} — n’existe plus au store</option>}
        </select>
        {action}
      </div>
      {groups === null && <div style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)', marginTop: 4 }}>Lecture des groupes au store…</div>}
      {noGroup && (
        <div data-no-private-store style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)', marginTop: 4, lineHeight: 1.45 }}>
          Les appareils de ce magasin ne suivent pas le store privé pour cette application : ils se mettent à jour par leur
          propre store, celui du fabricant de l’équipement. Le store privé ne leur proposera aucune version.
        </div>
      )}
      {error && <div style={{ fontSize: 'var(--t-sub)', color: 'var(--wa-fg)', marginTop: 4, lineHeight: 1.45, overflowWrap: 'anywhere' }}>Groupes du store illisibles — seuls le flux public et « aucun groupe » sont proposés : {error}</div>}
      {/* ⚠️ Un store qui répond SANS groupe se dit aussi : la liste réduite au flux public se lisait comme une panne. */}
      {!error && groups !== null && groups.length === 0 && (
        <div data-no-group style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)', marginTop: 4, lineHeight: 1.45 }}>
          Le store ne publie aucun groupe pour cette application : seuls le flux public et « aucun groupe » sont proposés. Un
          groupe se crée au store privé, dans les « Groupes de déploiement » de l’application.
        </div>
      )}
      {stale && <div style={{ fontSize: 'var(--t-sub)', color: 'var(--da-fg)', marginTop: 4, lineHeight: 1.45 }}>Ce groupe a été supprimé ou renouvelé au store : les appareils gardent le groupe qu’ils suivaient jusqu’à ce qu’on en choisisse un autre.</div>}
    </div>
  );
}

/** Un groupe retenu à la création : le jeton qui part au serveur, le nom que le récapitulatif affiche. */
export interface PickedGroup { token: string; name: string }

/**
 * Le choix d'un groupe dans l'assistant de création — rien n'est écrit avant l'exécution.
 * `value` absent = flux public.
 */
export function WizardUpdateGroup({ slug, label, value, onChange }: {
  slug: string; label: string; value: PickedGroup | undefined; onChange: (v: PickedGroup | undefined) => void;
}) {
  const { groups, error } = useAppGroups(slug);
  return (
    <UpdateGroupSelect label={label} groups={groups} error={error} value={value?.token ?? ''} staleName={value?.name}
      onChange={(t) => {
        if (t === NO_PRIVATE_STORE_UPDATES) { onChange({ token: t, name: NO_GROUP_LABEL }); return; }
        const g = groups?.find((x) => x.enrollToken === t);
        onChange(g ? { token: g.enrollToken, name: g.name } : undefined);
      }} />
  );
}

/** Une ligne de la carte : le groupe d'une application, modifiable. */
function AppGroupRow({ storeId, slug, label, choice, onSaved }: {
  storeId: string; slug: string; label: string; choice: UpdateGroupChoice | null; onSaved: () => void;
}) {
  const { groups, error } = useAppGroups(slug);
  const [value, setValue] = useState(choice?.enrollToken ?? '');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setValue(choice?.enrollToken ?? ''); }, [choice?.enrollToken]);
  const dirty = value !== (choice?.enrollToken ?? '');

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.setStoreUpdateGroup(storeId, slug, value || null);
      const name = r.choice?.enrollToken === NO_PRIVATE_STORE_UPDATES ? NO_GROUP_LABEL : r.choice?.groupName ?? 'flux public';
      // ⚠️ L'issue de la DESCENTE vient du serveur — une descente en échec n'est pas un succès à toaster.
      if (r.descent.ok) showToast(`${label} : « ${name} » — ${r.descent.note}.`);
      else await alert({ title: 'Choix enregistré, pas encore descendu', message: `${label} suivra « ${name} » dès que la descente aboutira : ${r.descent.note}. Redemander le même choix la rejoue.`, tone: 'warn' });
      onSaved();
    // ⚠️ ALERTE, PAS TOAST : un refus qui s'efface tout seul laisserait croire le groupe enregistré.
    } catch (e) { await alert({ title: 'Groupe non enregistré', message: (e as Error).message, tone: 'err' }); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ marginTop: 10 }}>
      <UpdateGroupSelect label={label} groups={groups} error={error} value={value} staleName={choice?.groupName} onChange={setValue} disabled={busy}
        action={<button className="btn btn-cta sm" onClick={() => void save()} disabled={busy || !dirty}>{busy ? '…' : 'Enregistrer'}</button>} />
    </div>
  );
}

/** Une application installée dans le magasin : ses appareils et leur version, et la version qu'ils devraient porter. */
export interface AppUpdateState {
  slug: string;
  label: string;
  devices: { code: string; version: string | null }[];
  /** La version attendue — `null` quand elle ne se mesure pas ; `from` dit alors pourquoi. */
  expected: string | null;
  from: string;
  /** Combien d'appareils portent la version attendue — `null` quand elle n'est pas connue (jamais « 0 à jour »). */
  upToDate: number | null;
}

/**
 * LA VERSION ATTENDUE d'une application, et d'où elle vient : le groupe choisi s'il sert une version, sinon le flux public.
 * ⚠️ « Aucun groupe » veut dire que l'appareil suit le store de son FABRICANT : on ne sait pas ce qu'il devrait porter, et
 *    on ne le juge pas. Un groupe disparu ou un store muet ne se jugent pas non plus.
 */
export function expectedVersion(choice: UpdateGroupChoice | null, groups: StoreGroup[] | null, publicVersion: string | null, publicLabel: string): { expected: string | null; from: string } {
  if (choice?.enrollToken === NO_PRIVATE_STORE_UPDATES) return { expected: null, from: 'store de l’appareil — version non suivie ici' };
  if (choice) {
    const g = groups?.find((x) => x.enrollToken === choice.enrollToken);
    if (!g) return { expected: null, from: `groupe « ${choice.groupName} » illisible au store` };
    if (g.servedVersion) return { expected: g.servedVersion, from: `groupe « ${g.name} »` };
  }
  return publicVersion ? { expected: publicVersion, from: choice ? `groupe « ${choice.groupName} » · flux public` : 'flux public' } : { expected: null, from: publicLabel };
}

/**
 * Le panneau « Mises à jour » de la fiche magasin : par application, le groupe suivi (modifiable), les versions installées
 * et ce qui est à jour.
 * ⚠️ LE GROUPE SE CHOISIT ICI, DANS LA CONSOLE (§32.6ter H) : la maquette de la fiche renvoie au store privé
 *    pour gérer les groupes — elle a raison pour les CRÉER, pas pour les CHOISIR ; le store n'a plus qu'un geste.
 * ⚠️ Monté aussi avant la mise en service : le choix vaut dès le premier scan.
 */
export function StoreUpdatesPanel({ storeId, apps, onSaved }: {
  storeId: string;
  /** Les applications à montrer, avec leurs appareils (une caisse n'a rien à faire sur un dépôt). */
  apps: { slug: string; devices: { code: string; version: string | null }[]; publicVersion: string | null; publicLabel: string }[];
  onSaved?: () => void;
}) {
  const [choices, setChoices] = useState<UpdateGroupChoice[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // ⚠️ UNE RÉPONSE DÉPASSÉE N'ÉCRIT PAS : changer de magasin pendant la lecture afficherait les groupes suivis par
  //    l'ANCIEN ; la relecture qui suit un enregistrement, croisée par celle de l'ouverture, remontrerait le groupe
  //    d'avant comme celui que suivent les appareils.
  const latest = useLatest();
  const load = useCallback(() => {
    const isLatest = latest();
    return api.storeUpdateGroups(storeId)
      .then((r) => { if (!isLatest()) return; setChoices(r.choices); setErr(null); })
      .catch((e) => { if (!isLatest()) return; setChoices(null); setErr((e as Error).message); });
  }, [storeId, latest]);
  useEffect(() => { setChoices(null); setErr(null); void load(); }, [load]);

  return (
    <section className="sp-panel">
      <header className="sp-ph"><div className="t"><h3>Applications des appareils</h3><p>Une ligne par application. Les appareils reçoivent seuls le groupe choisi : au scan de mise en service, puis à leur prochaine synchronisation. Les groupes se créent au store privé.</p></div></header>
      {err && <div className="sp-empty" style={{ color: 'var(--da-fg)', overflowWrap: 'anywhere' }}>{err}</div>}
      {!choices && !err && <div className="sp-empty">Lecture des groupes suivis…</div>}
      {choices && apps.map((a) => (
        <AppUpdateRow key={a.slug} storeId={storeId} app={a} choice={choices.find((c) => c.app === a.slug) ?? null} onSaved={() => { void load(); onSaved?.(); }} />
      ))}
    </section>
  );
}

/** Une application du panneau : son groupe, ses versions, son état. */
function AppUpdateRow({ storeId, app, choice, onSaved }: {
  storeId: string;
  app: { slug: string; devices: { code: string; version: string | null }[]; publicVersion: string | null; publicLabel: string };
  choice: UpdateGroupChoice | null;
  onSaved: () => void;
}) {
  const label = applicationOf(app.slug)?.name ?? app.slug;
  const { groups } = useAppGroups(app.slug);
  const { expected, from } = expectedVersion(choice, groups, app.publicVersion, app.publicLabel);
  const upToDate = expected === null ? null : app.devices.filter((d) => d.version === expected).length;
  const tone = app.devices.length === 0 ? 'ne' : upToDate === null ? 'ne' : upToDate === app.devices.length ? 'ok' : 'wa';
  return (
    <div className="sp-row wide" style={{ alignItems: 'start' }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 'var(--t-title)', fontWeight: 700 }}>{label}</div>
        <div className="mono" style={{ fontSize: 'var(--t-note)', color: 'var(--ink-700)', marginTop: 4, overflowWrap: 'anywhere' }}>
          {app.devices.length === 0 ? 'aucun appareil dans ce magasin' : app.devices.map((d) => `${d.code} ${d.version ?? 'version inconnue'}`).join(' · ')}
        </div>
        <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)', marginTop: 2 }}>
          {expected ? `attendue : ${expected} (${from})` : `version attendue non mesurée : ${from}`}
        </div>
        <AppGroupRow storeId={storeId} slug={app.slug} label="Groupe suivi" choice={choice} onSaved={onSaved} />
      </div>
      <span className={`sp-pill ${tone}`}>
        {app.devices.length === 0 ? 'aucun appareil' : upToDate === null ? 'non mesuré' : `${upToDate} / ${app.devices.length} à jour`}
      </span>
    </div>
  );
}
